import { Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CurrenciesService, Currency } from '@app/shared/services/currencies/currencies.service';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { PricingType, ProductCatalogService, ProductItem, ProductType } from '../../services/product-catalog.service';

@Component({
  standalone: false,
  selector: 'app-add-product-modal',
  template: `
    <div class="flex items-center justify-center min-h-screen p-4 relative">
      <sbnb-close [border]="true" (click)="close()" class="absolute top-4 right-4"></sbnb-close>
      <div class="w-full max-w-md bg-white p-6">
        <h2 class="text-2xl font-bold mb-6 text-center">Add New Product</h2>

        <div class="space-y-4">
          <div>
            <label for="productType" class="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
            <select
              id="productType"
              [(ngModel)]="selectedType"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-tour="product-type-select">
              <option value="early_checkin">Early Check-in</option>
              <option value="late_checkout">Late Check-out</option>
              <option disabled value="cleaning">Extra Cleaning (coming soon!)</option>
              <option value="custom">Other (add your own)</option>
            </select>
          </div>

          <div *ngIf="selectedType === 'custom'" class="space-y-4" data-tour="custom-product-fields">
            <div>
              <label for="productName" class="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
              <input
                id="productName"
                type="text"
                [(ngModel)]="customProductName"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter product name"
                data-tour="product-name-input" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Pricing Model</label>
              <div class="flex space-x-4" data-tour="pricing-model-radio">
                <label class="inline-flex items-center">
                  <input type="radio" [(ngModel)]="pricingType" value="fixed" class="form-radio text-blue-600" />
                  <span class="ml-2">Fixed Price</span>
                </label>
                <label class="inline-flex items-center">
                  <input type="radio" [(ngModel)]="pricingType" value="per_night" class="form-radio text-blue-600" />
                  <span class="ml-2">Per Night</span>
                </label>
              </div>
            </div>
          </div>

          <div *ngIf="currencies.length > 1">
            <label for="currency" class="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select
              id="currency"
              [(ngModel)]="selectedCurrency"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-tour="currency-select">
              <option *ngFor="let currency of currencies" [value]="currency.key">
                {{ currency.key }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            (click)="close()"
            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            Cancel
          </button>
          <sbnb-button
            [label]="'Save'"
            [type]="'primary'"
            (clicked)="save()"
            [loading]="isLoading"
            [disabled]="!isFormValid()"
            data-tour="save-button"></sbnb-button>
        </div>
      </div>
    </div>
  `,
})
export class AddProductModalComponent implements OnInit {
  selectedType: ProductType;
  selectedCurrency: string;
  customProductName: string = '';
  pricingType: PricingType = 'fixed';
  currencies: Currency[] = [];
  isLoading = false;

  constructor(
    private dialogRef: MatDialogRef<AddProductModalComponent>,
    private currenciesService: CurrenciesService,
    private productCatalogService: ProductCatalogService
  ) { }

  ngOnInit() {
    this.currenciesService
      .getCurrencies(true)
      .pipe(
        tap((currencies) => {
          this.currencies = currencies;
          if (currencies.length === 1) {
            this.selectedCurrency = currencies[0].key;
          }
        })
      )
      .subscribe();
  }

  isFormValid(): boolean {
    if (!this.selectedType || !this.selectedCurrency) return false;
    if (this.selectedType === 'custom') {
      return !!this.customProductName && !!this.pricingType;
    }
    return true;
  }

  save() {
    if (!this.isFormValid()) {
      console.error('Please fill in all required fields');
      return;
    }

    this.isLoading = true;
    this.productCatalogService
      .createNewProduct(
        this.selectedType,
        this.selectedCurrency,
        this.selectedType === 'custom' ? this.customProductName : undefined,
        this.selectedType === 'custom' ? this.pricingType : undefined
      )
      .pipe(
        map((response) => {
          this.isLoading = false;
          if (response.data) {
            this.dialogRef.close(response.data as ProductItem);
          } else {
            console.error('Failed to create product:', JSON.stringify(response));
          }
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Error creating product:', error);
          return of(error);
        })
      )
      .subscribe();
  }

  close() {
    this.dialogRef.close();
  }
}
